import { useState, useEffect } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Plus, Pencil, Search } from 'lucide-react';
import { AdminDeleteButton } from '../components/AdminDeleteButton';

const empty = { name: '', code: '', description: '', sort_order: 0 };

export default function CategoriesPage() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState('');

  const fetch = () => api.get('/categories').then(r => { setData(r.data); setLoading(false); }).catch(() => setLoading(false));
  useEffect(() => { fetch(); }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name || !form.code) { toast.error('Name and code are required'); return; }
    try {
      if (editing) { await api.put(`/categories/${editing}`, form); toast.success('Category updated'); }
      else { await api.post('/categories', form); toast.success('Category created'); }
      setOpen(false); setForm(empty); setEditing(null); fetch();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error saving'); }
  };

  const toggleStatus = async (c) => {
    const s = c.status === 'active' ? 'inactive' : 'active';
    await api.patch(`/categories/${c.category_id}/status`, { status: s }); toast.success(`Category ${s}`); fetch();
  };

  const edit = (c) => { setForm({ name: c.name, code: c.code, description: c.description || '', sort_order: c.sort_order || 0 }); setEditing(c.category_id); setOpen(true); };
  const filtered = data.filter(c => c.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <div data-testid="categories-page">
      <div className="page-header">
        <div><h1 className="page-title">Product Categories</h1><p className="text-sm text-slate-500 mt-1">{data.length} categories</p></div>
        <Button onClick={() => { setForm(empty); setEditing(null); setOpen(true); }} className="bg-slate-900 hover:bg-slate-800" data-testid="add-category-btn"><Plus size={16} className="mr-1.5" />Add Category</Button>
      </div>
      <div className="mb-4 relative max-w-sm"><Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" /><Input placeholder="Search categories..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 bg-white border-slate-200" data-testid="search-categories" /></div>
      <div className="data-table-wrapper">
        <Table>
          <TableHeader><TableRow className="bg-slate-50 border-b border-slate-200">
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Order</TableHead>
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Name</TableHead>
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Code</TableHead>
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Description</TableHead>
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Status</TableHead>
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {loading ? <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-400">Loading...</TableCell></TableRow> :
            filtered.length === 0 ? <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-400">No categories</TableCell></TableRow> :
            filtered.map(c => (
              <TableRow key={c.category_id} className="hover:bg-slate-50/50">
                <TableCell className="text-sm text-slate-500">{c.sort_order}</TableCell>
                <TableCell className="font-medium text-slate-900">{c.name}</TableCell>
                <TableCell className="font-mono text-xs text-slate-600">{c.code}</TableCell>
                <TableCell className="text-sm text-slate-600">{c.description}</TableCell>
                <TableCell><button onClick={() => toggleStatus(c)} className={`status-badge cursor-pointer status-${c.status}`}>{c.status}</button></TableCell>
                <TableCell><div className="flex items-center gap-1"><Button variant="ghost" size="sm" onClick={() => edit(c)}><Pencil size={14} /></Button><AdminDeleteButton itemName={c.name} description={`Permanently delete category "${c.name}". This cannot be undone.`} onConfirm={() => remove(c.category_id)} testId={`delete-category-${c.category_id}`} /></div></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-heading">{editing ? 'Edit Category' : 'Add Category'}</DialogTitle></DialogHeader>
          <form onSubmit={handleSave} className="space-y-4 mt-2">
            <div className="form-field"><Label>Name *</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="bg-slate-50" data-testid="category-name-input" /></div>
            <div className="form-field"><Label>Code *</Label><Input value={form.code} onChange={e => setForm({...form, code: e.target.value})} className="bg-slate-50" data-testid="category-code-input" /></div>
            <div className="form-field"><Label>Description</Label><Input value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="bg-slate-50" /></div>
            <div className="form-field"><Label>Sort Order</Label><Input type="number" value={form.sort_order} onChange={e => setForm({...form, sort_order: parseInt(e.target.value) || 0})} className="bg-slate-50" /></div>
            <div className="flex justify-end gap-2 pt-2"><Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="save-category-btn">Save</Button></div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
